/**
 * useDynamicTaxonomyTranslation
 *
 * Takes the categories array fetched from /api/categories/db and ensures
 * every category + subcategory name has a translation in the current
 * i18n language. New DB-added entries that don't yet have a static i18n
 * key get translated on-the-fly via /api/translate/batch (cached server-
 * side + in localStorage), then added to the i18next resource bundle so
 * any `t('cat.<id>')` / `t('sub.<id>')` call across the app picks them
 * up automatically.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANGUAGE_NAMES = {
  en: 'English', ar: 'Arabic', zh: 'Chinese', nl: 'Dutch', fr: 'French',
  de: 'German', hi: 'Hindi', id: 'Indonesian', it: 'Italian', ja: 'Japanese',
  ko: 'Korean', ms: 'Malay', pl: 'Polish', pt: 'Portuguese', ru: 'Russian',
  es: 'Spanish', sv: 'Swedish', th: 'Thai', tr: 'Turkish', vi: 'Vietnamese',
};

export const useDynamicTaxonomyTranslation = (categories = []) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  useEffect(() => {
    if (!categories || categories.length === 0) return;
    if (currentLang === 'en') return; // English uses DB names directly

    const langName = LANGUAGE_NAMES[currentLang] || currentLang;
    const bundle = i18n.getResourceBundle(currentLang, 'translation') || {};

    // Collect every cat/sub key that's still missing in this language
    const missing = {};
    for (const cat of categories) {
      const ck = `cat.${cat.id}`;
      if (!bundle[ck] && cat.name) missing[ck] = cat.name;
      for (const sub of cat.subcategories || []) {
        const sk = `sub.${sub.id}`;
        if (!bundle[sk] && sub.name) missing[sk] = sub.name;
      }
    }

    if (Object.keys(missing).length === 0) return;

    // localStorage cache — NON-SENSITIVE public taxonomy translations.
    // Do NOT store auth tokens, PII, or payment data here.
    const cacheKey = `taxonomy_translations_v1_${currentLang}`;
    let cached = {};
    try { cached = JSON.parse(localStorage.getItem(cacheKey) || '{}'); }
    catch (err) { console.debug('[taxonomyTranslate] cache parse failed:', err?.message); cached = {}; }

    // Apply anything already cached locally
    const applyCached = {};
    const stillMissing = {};
    for (const [k, src] of Object.entries(missing)) {
      if (cached[k]) applyCached[k] = cached[k];
      else stillMissing[k] = src;
    }
    if (Object.keys(applyCached).length > 0) {
      i18n.addResourceBundle(currentLang, 'translation', applyCached, true, true);
    }

    if (Object.keys(stillMissing).length === 0) return;

    // Fetch translations for what's still missing
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/translate/batch`, {
          texts: stillMissing,
          target_language: langName,
        });
        if (cancelled) return;
        const fresh = res.data?.translations || {};
        if (Object.keys(fresh).length > 0) {
          i18n.addResourceBundle(currentLang, 'translation', fresh, true, true);
          const merged = { ...cached, ...fresh };
          try { localStorage.setItem(cacheKey, JSON.stringify(merged)); }
          catch (err) { console.debug('[taxonomyTranslate] cache write failed (quota?):', err?.message); }
          // Force a re-render of consumers by emitting languageChanged
          i18n.emit('languageChanged', currentLang);
        }
      } catch (err) {
        console.debug('Taxonomy translation failed:', err?.message);
      }
    })();

    return () => { cancelled = true; };
  }, [categories, currentLang, i18n]);
};

export default useDynamicTaxonomyTranslation;
